import React from 'react';
import tokensData from '../../data/tokens.json';
import { calculateTokenValueUSD, MARKET_PRICES } from '../../constants/pricing';

const AssetsPage = ({ profileData }) => {
  const tokens = tokensData.tokens;
  const balances = profileData.balances || {};
  const stakedPositions = profileData.stakedPositions || [];
  const hasStakedPositions = stakedPositions.length > 0;
  
  // Mock withdrawal requests for demo (in real app, this would come from profileData)
  const withdrawalRequests = hasStakedPositions ? [
    {
      id: 837,
      nIcpBurned: 1000.73,
      icpDue: 1108.57,
      neuronId: '994608395738904891',
      status: 'Waiting Dissolvement'
    },
    {
      id: 838,
      nIcpBurned: 993.21,
      icpDue: 1100.24,
      neuronId: '123271224764318392',
      status: 'Waiting Dissolvement'
    }
  ] : [];

  // Calculate total balance (simplified calculation)
  const tokenBalance = Object.entries(balances).reduce((total, [ticker, amount]) => {
    return total + calculateTokenValueUSD(ticker, amount);
  }, 0);
  
  // Add staked positions value (excluding nICP which is already in regular tokens)
  const stakedPositionsValue = stakedPositions.reduce((total, position) => {
    if (position.asset === 'nICP') {
      return total; // Don't double count nICP - it's already in the regular tokens list
    }
    return total + calculateTokenValueUSD(position.asset, position.amount);
  }, 0);
  
  // Add withdrawal requests value
  const withdrawalRequestsValue = withdrawalRequests.reduce((total, request) => {
    return total + (request.icpDue * MARKET_PRICES.ICP);
  }, 0);
  
  const totalBalance = tokenBalance + stakedPositionsValue + withdrawalRequestsValue;
  const hasTokens = Object.keys(balances).length > 0;

  const getTokenIcon = (ticker) => {
    const iconMap = {
      'ckBTC': '₿',
      'ETH': 'Ξ', 
      'ICP': '∞',
      'nICP': '💧',
      'PANDA': '🐼',
      'ckUSDT': '$',
      'GLDT': '🥇'
    };
    return iconMap[ticker] || '●';
  };

  const formatBalance = (amount) => {
    if (amount >= 1000) {
      return amount.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    }
    return amount.toFixed(4);
  };

  const getTokenValue = (ticker, amount) => {
    const value = calculateTokenValueUSD(ticker, amount);
    return `$${value.toFixed(2)}`;
  };

  return (
    <div>
      <div className="balance-card">
        <div className="balance-amount">${totalBalance.toFixed(2)}</div>
        <div className="balance-label">Your balance</div>
        <div className="action-buttons">
          <button className="action-button">
            📱 Receive
          </button>
          <button className="action-button">
            📤 Send
          </button>
          <button className="action-button">
            🔄 Swap
          </button>
          <button className="action-button">
            🛒 Buy
          </button>
        </div>
      </div>

      <div className="tokens-section">
        <div className="section-header">
          <h2 className="section-title">Tokens</h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="action-button">🔍</button>
            <button className="action-button">🔽</button>
          </div>
        </div>

        {!hasTokens ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <div className="empty-title">No tokens yet</div>
            <div className="empty-description">
              Get started by receiving your first tokens or buying some to begin your crypto journey.
            </div>
            <button className="get-started-button">+ Get Started</button>
          </div>
        ) : (
          <ul className="token-list">
            {tokens.map((token) => {
              const balance = balances[token.ticker];
              if (!balance) return null;
              
              return (
                <li key={token.ticker} className="token-item">
                  <div className="token-icon">
                    {getTokenIcon(token.ticker)}
                  </div>
                  <div className="token-info">
                    <div className="token-name">
                      {token.ticker}
                      {token.stakeable && <span className="stakeable-badge">⭐ Stakeable</span>}
                    </div>
                    <div className="token-network">{token.network} • {token.name}</div>
                  </div>
                  <div className="token-balance">
                    <div className="token-amount">{formatBalance(balance)}</div>
                    <div className="token-value">{getTokenValue(token.ticker, balance)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Withdrawal Requests Section */}
      {withdrawalRequests.length > 0 && (
        <div className="staked-positions">
          <h2 className="section-title">Withdrawal Requests</h2>
          
          <div className="positions-section">
            <div className="positions-table withdrawing assets-view">
              <div className="table-header">
                <div className="header-cell">nICP Burned</div>
                <div className="header-cell">ICP Due</div>
                <div className="header-cell">Neuron Id</div>
                <div className="header-cell">Status</div>
                <div className="header-cell">Id</div>
              </div>
              {withdrawalRequests.map((request) => (
                <div key={request.id} className="table-row">
                  <div className="table-cell">
                    <div className="token-balance">
                      <div className="token-amount">{request.nIcpBurned.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                      <div className="token-value">${(request.nIcpBurned * MARKET_PRICES.nICP).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div className="table-cell">
                    <div className="token-balance">
                      <div className="token-amount">{request.icpDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                      <div className="token-value">${(request.icpDue * MARKET_PRICES.ICP).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div className="table-cell">
                    <span className="neuron-id">{request.neuronId}</span>
                  </div>
                  <div className="table-cell">
                    <span className="status-badge waiting">{request.status}</span>
                  </div>
                  <div className="table-cell">
                    <span className="request-id">{request.id}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsPage;
